import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import { Mail, ArrowRight, ArrowLeft, Shield, CheckCircle } from 'lucide-react';

export default function EmailAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  
  const initialRole = searchParams.get('role') || 'client';
  
  const [step, setStep] = useState('email'); // email, code
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isClient = role === 'client';
  const themeColor = isClient ? '#0D7377' : '#FF6B35';

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/email/send-code', { email, role });
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/email/verify-code', { email, code, role });
      if (response.data.success) {
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        setUser(response.data.user);
        const redirectPath = response.data.user.role === 'researcher'
          ? '/researcher/dashboard'
          : '/client/dashboard';
        navigate(redirectPath, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-neutral-900 rounded-sm flex items-center justify-center text-white font-bold text-sm">RE</div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Outfit' }}>Research Exchange</span>
          </Link>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
            Sign in with Email
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            {isClient ? 'Use your official company email' : 'Enter your email to get started'}
          </p>
        </div>

        {/* Role Toggle */}
        <div className="flex gap-1 bg-neutral-200 p-1 mb-6">
          <button
            onClick={() => { setRole('researcher'); setError(''); }}
            data-testid="role-researcher"
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              role === 'researcher' ? 'bg-white text-[#FF6B35] shadow-sm' : 'text-neutral-500'
            }`}
          >
            Researcher
          </button>
          <button
            onClick={() => { setRole('client'); setError(''); }}
            data-testid="role-client"
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              role === 'client' ? 'bg-white text-[#0D7377] shadow-sm' : 'text-neutral-500'
            }`}
          >
            Client
          </button>
        </div>

        <div className="bg-white border border-neutral-200 p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-4" data-testid="auth-error">
              {error}
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSendCode}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  <Mail size={14} className="inline mr-1" />
                  {isClient ? 'Official Company Email' : 'Email Address'}
                </label>
                <input
                  type="email"
                  data-testid="email-input"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isClient ? 'you@yourcompany.com' : 'you@example.com'}
                  className="w-full px-4 py-3 border border-neutral-200 focus:border-current outline-none text-sm"
                  style={{ '--tw-ring-color': themeColor }}
                />
                {isClient && (
                  <p className="text-xs text-neutral-400 mt-1.5 flex items-start gap-1">
                    <Shield size={12} className="mt-0.5 flex-shrink-0" />
                    We require official company emails (not Gmail, Yahoo, etc.) to verify your organisation.
                  </p>
                )}
              </div>
              <button
                type="submit"
                data-testid="send-code-btn"
                disabled={loading || !email}
                className="w-full py-3 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: themeColor }}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>Send Verification Code <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode}>
              <div className="text-center mb-4">
                <CheckCircle size={32} className="mx-auto mb-2" style={{ color: themeColor }} />
                <p className="text-sm text-neutral-600">
                  We sent a 6-digit code to<br />
                  <strong>{email}</strong>
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Verification Code</label>
                <input
                  type="text"
                  data-testid="code-input"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-neutral-200 focus:border-current outline-none text-center text-2xl tracking-[0.5em] font-mono"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                data-testid="verify-code-btn"
                disabled={loading || code.length !== 6}
                className="w-full py-3 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: themeColor }}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Verify & Sign In'
                )}
              </button>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(''); }}
                className="w-full py-2 mt-2 text-sm text-neutral-500 hover:text-neutral-700 flex items-center justify-center gap-1"
              >
                <ArrowLeft size={14} /> Use a different email
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6 space-y-3">
          <p className="text-sm text-neutral-500">
            Or sign in with{' '}
            <button onClick={() => {
              localStorage.setItem('pendingRole', role);
              const redirectUrl = window.location.origin + '/auth/callback';
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }} className="font-medium hover:underline" style={{ color: themeColor }}>
              Google
            </button>
          </p>
          <Link to="/" className="text-xs text-neutral-400 hover:text-neutral-600 block">
            Back to Research Exchange
          </Link>
        </div>
      </div>
    </div>
  );
}
